/**
 * Design tokens for Evaluate / Execute verdict pills.
 */

export type VerdictKey = 'green' | 'cond' | 'hold';

export interface VerdictToken {
  key: VerdictKey;
  label: string;
  short: string;
  verb: string;
  bg: string;
  fg: string;
  dot: string;
}

export const VERDICTS: Record<VerdictKey, VerdictToken> = {
  green: { key: 'green', label: 'Green Light — Proceed', short: 'Green Light', verb: 'Proceed', bg: '#dcfce7', fg: '#15803d', dot: '#16a34a' },
  cond: { key: 'cond', label: 'Conditional — Validate First', short: 'Conditional', verb: 'Validate', bg: '#fef3c7', fg: '#b45309', dot: '#d97706' },
  hold: { key: 'hold', label: 'Hold — Revisit Later', short: 'Hold', verb: 'Defer', bg: '#e2e8f0', fg: '#475569', dot: '#64748b' },
};

export const VERDICT_BANNER_ALPHA = { border: '55', background: '66' } as const;

/** Execute-path initiative status. Includes this branch’s API statuses plus storefront’s CFO Review. */
export type ExecStatus =
  | 'Active'
  | 'CFO Review'
  | 'Activation Required'
  | 'Approval Pending'
  | 'Approved'
  | 'Planning'
  | 'At Risk'
  | 'On Hold'
  | 'Complete'
  | 'Value Tracking';

export const EXEC_STATUSES: Record<ExecStatus, { bg: string; fg: string; dot: string }> = {
  Active: { bg: '#ecfdf5', fg: '#047857', dot: '#10b981' },
  'CFO Review': { bg: '#fffbeb', fg: '#b45309', dot: '#f59e0b' },
  'Activation Required': { bg: '#eef2ff', fg: '#4338ca', dot: '#6366f1' },
  'Approval Pending': { bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8' },
  Approved: { bg: '#eff6ff', fg: '#1d4ed8', dot: '#3b82f6' },
  Planning: { bg: '#eef2ff', fg: '#4338ca', dot: '#6366f1' },
  'At Risk': { bg: '#fff1f2', fg: '#be123c', dot: '#f43f5e' },
  'On Hold': { bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8' },
  Complete: { bg: '#f0fdfa', fg: '#0f766e', dot: '#14b8a6' },
  'Value Tracking': { bg: '#faf5ff', fg: '#7e22ce', dot: '#a855f7' },
};

export const MILESTONE_STATUS_COLORS: Record<string, string> = {
  Complete: '#059669',
  'In Progress': '#2563eb',
  'Not Started': '#94a3b8',
  'At Risk': '#d97706',
  Delayed: '#e11d48',
};

/** Severity dot classes. 'Moderate' is the Execute-path spelling of 'Medium'. */
export const SEVERITY_DOT: Record<string, string> = {
  Critical: 'bg-red-600',
  High: 'bg-orange-500',
  Medium: 'bg-amber-500',
  Moderate: 'bg-amber-500',
  Low: 'bg-emerald-500',
};

/** Shared Evaluate verdict: numeric composite first, then value-band fallback. */
export function verdictFromScore(score?: number, valueBand?: string): VerdictKey {
  if (typeof score === 'number' && Number.isFinite(score) && score > 0) {
    if (score >= 72) return 'green';
    if (score >= 55) return 'cond';
    return 'hold';
  }
  const b = (valueBand ?? '').trim().toLowerCase();
  if (b === 'high' || b === 'significant') return 'green';
  if (b === 'moderate') return 'cond';
  return 'hold';
}

export type RecommendationKey = 'PROCEED' | 'EVALUATE' | 'DEFER';

export function recommendationFromVerdict(verdict: VerdictKey): RecommendationKey {
  if (verdict === 'green') return 'PROCEED';
  if (verdict === 'cond') return 'EVALUATE';
  return 'DEFER';
}

/**
 * Explicit recommendation / verdict from the evaluation API (DMD-1160).
 * Returns null when the payload carries none, so callers can tell an API decision
 * apart from a score-threshold fallback.
 */
export function parseRecommendation(raw: unknown): RecommendationKey | null {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  if (s.includes('no-go') || s.includes('no go') || s.includes('defer') || s.includes('hold')) return 'DEFER';
  if (s.includes('proceed') || s.includes('green') || s === 'go') return 'PROCEED';
  if (
    s.includes('evaluate') ||
    s.includes('conditional') ||
    s.includes('cond') ||
    s.includes('validate') ||
    s.includes('amber')
  ) {
    return 'EVALUATE';
  }
  return null;
}
